/**
 * Re-attach to a turn that was already running when this chat mounted.
 *
 * The case this exists for: the user sends a message, the tab reloads or is
 * reclaimed by the OS mid-answer, and they come back. The server never stopped
 * — the turn completes and persists either way — but before this the UI showed
 * nothing until the whole turn landed, which reads as a dropped request.
 *
 * Gated on "the newest message is from the user". A completed turn always ends
 * with an assistant message, so that condition means a turn was sent and its
 * reply has not been persisted yet — in flight, or failed. Attempting resume in
 * the failed case is harmless: the server answers 204 and nothing happens.
 * Without the gate every chat open would cost a request that is almost always 204.
 *
 * Fires ONCE per chatId. Resume is a mount-time recovery, not a retry loop; if
 * it fails, the user still has Retry and the turn is still persisted server-side.
 */

import { useEffect, useRef } from 'react';
import type { UIMessage } from 'ai';
import { logger } from '../../lib/logger.js';

export interface UseStreamResumeOptions {
  chatId: string;
  messages: UIMessage[];
  /** `resumeStream` from useChat. */
  resumeStream: () => Promise<void>;
  /**
   * Whether the chat is ready to resume. Callers pass `false` while initial
   * history is still loading, so the gate below is not evaluated against an
   * empty list (which would look like "nothing to resume" and skip a live turn).
   */
  enabled: boolean;
}

/** Does the message list look like a turn that never got its reply? */
export function hasUnfinishedTurn(messages: UIMessage[]): boolean {
  const last = messages[messages.length - 1];
  return last?.role === 'user';
}

export function useStreamResume({
  chatId,
  messages,
  resumeStream,
  enabled,
}: UseStreamResumeOptions): void {
  const attemptedRef = useRef<string | null>(null);
  // Read messages through a ref so this effect depends on `chatId`/`enabled`
  // only — depending on `messages` would re-run it on every streamed token.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    if (!enabled) return;
    if (attemptedRef.current === chatId) return;
    attemptedRef.current = chatId;

    if (!hasUnfinishedTurn(messagesRef.current)) return;

    logger.debug('attempting stream resume', { chatId });

    void resumeStream().catch((error: unknown) => {
      // Non-fatal by construction: the turn is persisted server-side regardless,
      // so a failed resume means the user waits for the reload rather than
      // seeing an error.
      logger.warn('stream resume failed', { chatId, error: String(error) });
    });
  }, [chatId, enabled, resumeStream]);
}
